/**
 * LeadActivityPanel - Activity feed tab panel (emails / SMS / calls / recordings) for a lead.
 * Extracted verbatim from LeadDetailPage.jsx (behavior unchanged); state/handlers passed as props.
 */
import React from 'react';
import { format, parseISO } from 'date-fns';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { History, Mail, MessageSquare, Phone, Mic, User, Reply, Forward, PhoneCall, FileText, Paperclip, Trash2, Sparkles, RefreshCw } from 'lucide-react';
import { formatPhone } from '../../utils/formatPhone';
import EmailTrackingBadges from '../EmailTrackingBadges';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Clean email content by removing HTML artifacts and MSO styles
 */
const cleanEmailContent = (content) => {
  if (!content) return '';

  let text = content;

  // Remove inline style attributes that weren't fully stripped
  text = text.replace(/style\s*=\s*['"][^'"]*['"]/gi, '');

  // Remove MSO-specific patterns
  text = text.replace(/mso-[^;:\s]+[:\s][^;'"]+[;]?/gi, '');

  // Remove leftover HTML attributes
  text = text.replace(/class\s*=\s*['"][^'"]*['"]/gi, '');

  // Remove remaining HTML tags that slipped through
  text = text.replace(/<[^>]+>/g, '');

  // Clean up CSS property values that leaked (e.g., position:absolute;margin-left:0)
  text = text.replace(/\b(?:position|margin|padding|width|height|z-index|visibility|display|font-family|font-size|color|background|border)\s*:\s*[^;\s]+[;]?\s*/gi, '');

  // Remove CSS units and values patterns
  text = text.replace(/\b\d+(?:\.\d+)?(?:pt|px|em|rem|%)\b/gi, '');

  // Clean up excessive whitespace
  text = text.replace(/\s+/g, ' ').trim();

  // Remove lines that are just CSS garbage
  const lines = text.split('\n');
  const cleanLines = lines.filter(line => {
    const trimmed = line.trim();
    // Skip lines that are mostly CSS/style remnants
    if (trimmed.length < 3) return false;
    if (/^[;\s\-:,\.]+$/.test(trimmed)) return false;
    if (/^\d+(?:\.\d+)?$/.test(trimmed)) return false;
    return true;
  });

  return cleanLines.join('\n').trim();
};

export default function LeadActivityPanel({
  activityFilter,
  setActivityFilter,
  communications,
  filteredCommunications,
  lead,
  comms,
  handleCleanEmails,
  setSyncEmailsDialogOpen,
  handleEmailClick,
  handleSmsReplyClick,
  handleCallClick,
  handleLogCallClick,
}) {
  return (
    <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {['all', 'email', 'sms', 'call', 'call_recording'].map(filter => (
                      <Button key={filter} variant={activityFilter === filter ? 'default' : 'outline'} size="sm" onClick={() => setActivityFilter(filter)} data-testid={`filter-${filter}`}>
                        {filter === 'all' ? 'All' : filter === 'call_recording' ? `Recordings (${communications.filter(c => c.type === 'call_recording' || c.has_recording).length})` : `${filter.charAt(0).toUpperCase() + filter.slice(1)} (${communications.filter(c => c.type === filter).length})`}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {communications.some(c => c.type === 'email' && c.synced_from_o365) && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleCleanEmails}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        data-testid="clean-emails-btn"
                        title="Clean HTML artifacts from synced emails"
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        Clean
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSyncEmailsDialogOpen(true)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="sync-emails-btn"
                      disabled={!lead.work_email && !lead.personal_email}
                      title={!lead.work_email && !lead.personal_email ? "No email address for this lead" : "Sync old emails from Office 365"}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Sync Old Emails
                    </Button>
                  </div>
                </div>
                {filteredCommunications.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No activity yet. Start by sending an email or making a call.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCommunications.map(comm => (
                      <div 
                        key={comm.id} 
                        className={`flex gap-3 p-3 border rounded-lg transition-colors group ${comm.type === 'email' ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/50' : comm.type === 'sms' ? 'hover:border-green-300 hover:bg-green-50/50' : comm.type === 'call' ? 'hover:border-amber-300 hover:bg-amber-50/50' : 'hover:border-indigo-200'}`}
                        onClick={() => comm.type === 'email' && handleEmailClick(comm, 'reply')}
                        data-testid={`activity-${comm.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${comm.type === 'email' ? 'bg-blue-100' : comm.type === 'sms' ? 'bg-green-100' : comm.type === 'call_recording' ? 'bg-purple-100' : 'bg-amber-100'}`}>
                          {comm.type === 'email' && <Mail className="h-5 w-5 text-blue-600" />}
                          {comm.type === 'sms' && <MessageSquare className="h-5 w-5 text-green-600" />}
                          {comm.type === 'call' && <Phone className="h-5 w-5 text-amber-600" />}
                          {comm.type === 'call_recording' && <Mic className="h-5 w-5 text-purple-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{comm.type === 'email' ? comm.subject : comm.type === 'sms' ? `SMS to ${formatPhone(comm.to)}` : comm.type === 'call_recording' ? 'Recording' : `Call to ${formatPhone(comm.to || comm.phone_number)}`}</span>
                            {comm.call_outcome && <Badge variant="outline" className="text-xs">{comm.call_outcome}</Badge>}
                            {comm.has_recording && <Badge className="bg-purple-100 text-purple-700 text-xs">Has Recording</Badge>}
                            {comm.type === 'call_recording' && <Badge className="bg-purple-100 text-purple-700 text-xs">Recording</Badge>}
                            {comm.type === 'email' && comm.direction === 'inbound' && <Badge className="bg-blue-100 text-blue-700 text-xs">Received</Badge>}
                            {comm.type === 'email' && comm.direction !== 'inbound' && comm.email_sent === true && <Badge className="bg-green-100 text-green-700 text-xs">Sent</Badge>}
                            {comm.type === 'email' && comm.direction !== 'inbound' && comm.email_sent === false && <Badge className="bg-amber-100 text-amber-700 text-xs">Logged Only</Badge>}
                            {comm.synced_from_o365 && <Badge className="bg-indigo-100 text-indigo-700 text-xs">O365</Badge>}
                            <EmailTrackingBadges comm={comm} />
                            {comm.sms_sent === true && <Badge className="bg-green-100 text-green-700 text-xs">Sent</Badge>}
                            {comm.sms_sent === false && comm.type === 'sms' && <Badge className="bg-amber-100 text-amber-700 text-xs">Logged Only</Badge>}
                          </div>
                          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{comm.type === 'email' ? cleanEmailContent(comm.content) : comm.content}</p>
                          {(comm.has_recording || comm.type === 'call_recording') && comm.recording_id && (
                            <div className="mt-2 p-2 bg-slate-50 rounded-lg" onClick={(e) => e.stopPropagation()}>
                              <audio controls className="w-full h-8" src={`${API}/telnyx/recordings/play/${comm.recording_id}`}>Your browser does not support the audio element.</audio>
                              {comm.duration_secs && <p className="text-xs text-slate-500 mt-1">Duration: {Math.floor(comm.duration_secs / 60)}:{(comm.duration_secs % 60).toString().padStart(2, '0')}</p>}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-slate-500 flex items-center gap-2">
                              <User className="h-3 w-3" />
                              {comm.user_name} • {comm.created_at ? format(parseISO(comm.created_at), 'MMM d, yyyy h:mm a') : ''}
                              {comm.direction === 'inbound' && comm.from_email && <span className="text-blue-600">• From: {comm.from_email}</span>}
                              {comm.direction === 'outbound' && comm.to && <span className="text-green-600">• To: {comm.to}</span>}
                            </p>
                            
                            {/* Hover action icons for emails */}
                            {comm.type === 'email' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className="p-1.5 rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-100 transition-colors"
                                  onClick={() => handleEmailClick(comm, 'reply')}
                                  data-testid={`reply-${comm.id}`}
                                  title="Reply"
                                >
                                  <Reply className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-1.5 rounded-md text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 transition-colors"
                                  onClick={() => handleEmailClick(comm, 'forward')}
                                  data-testid={`forward-${comm.id}`}
                                  title="Forward"
                                >
                                  <Forward className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            
                            {/* Hover action icons for SMS */}
                            {comm.type === 'sms' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className="p-1.5 rounded-md text-green-600 hover:text-green-700 hover:bg-green-100 transition-colors"
                                  onClick={() => handleSmsReplyClick(comm)}
                                  data-testid={`sms-reply-${comm.id}`}
                                  title="Reply to SMS"
                                >
                                  <Reply className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            
                            {/* Hover action icons for calls */}
                            {comm.type === 'call' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className="p-1.5 rounded-md text-amber-600 hover:text-amber-700 hover:bg-amber-100 transition-colors"
                                  onClick={() => handleCallClick(comm)}
                                  data-testid={`call-${comm.id}`}
                                  title="Call this lead"
                                  disabled={lead.dnc}
                                >
                                  <PhoneCall className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-1.5 rounded-md text-slate-600 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                  onClick={() => handleLogCallClick(comm)}
                                  data-testid={`log-call-${comm.id}`}
                                  title="Log a call"
                                >
                                  <FileText className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          {comm.attachments && comm.attachments.length > 0 && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <Paperclip className="h-3 w-3 text-slate-400" />
                              {comm.attachments.map((att, i) => (<Badge key={i} variant="outline" className="text-xs bg-slate-50">{att}</Badge>))}
                            </div>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); comms.handleDeleteCommunication(comm.id); }} data-testid={`delete-comm-${comm.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
    </>
  );
}
